import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronLeft, ChevronRight, XIcon } from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { cn } from '@/libs/utils/cn';

// Context for DialogContent <-> DialogPanel communication
interface DialogPanelContextValue {
  panelState: { isOpen: boolean; side: 'left' | 'right' } | null;
  setPanelState: (state: { isOpen: boolean; side: 'left' | 'right' } | null) => void;
}

const DialogPanelContext = createContext<DialogPanelContextValue | null>(null);

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className,
      )}
      {...props}
    />
  );
}

const dialogContentVariants = cva(
  'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed z-50 w-full gap-4 rounded-[10px] border border-border p-6 shadow-modal duration-200 outline-hidden',
  {
    variants: {
      position: {
        default:
          'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        bl: 'left-6 bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        bc: 'left-[50%] top-[50%] translate-x-[-50%] bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        br: 'right-6 bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        tl: 'left-6 top-6 data-[state=closed]:slide-out-to-top-16 data-[state=open]:slide-in-from-top-16',
        tr: 'right-6 top-6 data-[state=closed]:slide-out-to-top-16 data-[state=open]:slide-in-from-top-16',
      },
      size: {
        default: 'max-w-lg',
        xs: 'max-w-3xs',
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-xl',
        xl: 'max-w-2xl',
        fullscreen: 'w-screen h-screen',
      },
    },
    defaultVariants: {
      size: 'md',
      position: 'default',
    },
  },
);

/** Replaces `left-[50%]` when a dialog should track `--dialog-anchor-x` (map sidebar). */
const DIALOG_VIEWPORT_ANCHOR_CLASS = 'left-[var(--dialog-anchor-x,50%)]';

function DialogContent({
  className,
  children,
  showOverlay = true,
  showClose = true,
  overlayClassName,
  size,
  position,
  dialogContainer,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogContentVariants> & {
    showOverlay?: boolean;
    /** `false` for a card that draws its own close button. */
    showClose?: boolean;
    /** Extra classes for the overlay — e.g. `absolute inset-0` to scope it to `dialogContainer` instead of the viewport. */
    overlayClassName?: string;
    dialogContainer?: HTMLElement;
  }) {
  const [panelState, setPanelState] = useState<{
    isOpen: boolean;
    side: 'left' | 'right';
  } | null>(null);

  const contextValue = useMemo(() => ({ panelState, setPanelState }), [panelState]);

  // An open panel squares the card's corners on its side; the card keeps its shadow so the
  // pair casts one. A card whose surface is a child reads `data-panel-side` through `group/dialog`.
  const panelSide = panelState?.isOpen ? panelState.side : undefined;
  const panelOpenClasses =
    panelSide && (panelSide === 'right' ? 'rounded-r-none' : 'rounded-l-none');

  return (
    <DialogPanelContext.Provider value={contextValue}>
      <DialogPortal data-slot="dialog-portal" container={dialogContainer}>
        {showOverlay && <DialogOverlay className={overlayClassName} />}
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            dialogContentVariants({ size, position }),
            'group/dialog transition-all duration-300',
            panelOpenClasses,
            className,
          )}
          {...props}
          data-position={position ?? 'default'}
          data-panel-side={panelSide}
        >
          {children}
          {showClose && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPanelContext.Provider>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

const dialogPanelVariants = cva(
  'bg-background absolute -top-px -bottom-px border border-border transition-all duration-300 ease-in-out overflow-hidden shadow-modal',
  {
    variants: {
      // The panel overlaps the card's border by a pixel and clips its shadow on that side, so the
      // seam shows neither a line nor a shade.
      side: {
        left: 'right-[calc(100%-1px)] rounded-l-[10px] border-r-0 [clip-path:inset(-24px_0_-24px_-24px)]',
        right:
          'left-[calc(100%-1px)] rounded-r-[10px] border-l-0 [clip-path:inset(-24px_-24px_-24px_0)]',
      },
      isOpen: {
        true: 'opacity-100',
        false: 'opacity-0 pointer-events-none',
      },
    },
    compoundVariants: [
      { side: 'left', isOpen: true, className: 'translate-x-0' },
      { side: 'left', isOpen: false, className: 'translate-x-4' },
      { side: 'right', isOpen: true, className: 'translate-x-0' },
      { side: 'right', isOpen: false, className: '-translate-x-4' },
    ],
    defaultVariants: {
      side: 'right',
      isOpen: false,
    },
  },
);

interface DialogPanelProps extends React.ComponentProps<'div'> {
  /** Controlled mode: externally controlled open state */
  isOpen?: boolean;
  /** Controlled mode: callback when toggle is clicked */
  onOpenChange?: (isOpen: boolean) => void;
  /** Default open state for uncontrolled mode */
  defaultOpen?: boolean;
  /** Which side the panel opens to */
  side?: 'left' | 'right';
  /** Vertical position of the toggle button */
  buttonPosition?: 'top' | 'bottom';
  width?: string;
}

function DialogPanel({
  className,
  children,
  isOpen: controlledIsOpen,
  onOpenChange,
  defaultOpen = false,
  side = 'right',
  buttonPosition = 'top',
  width = 'w-64',
  ...props
}: DialogPanelProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const context = useContext(DialogPanelContext);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  // Sync panel state with DialogContent via context
  const { setPanelState } = context ?? {};
  useEffect(() => {
    setPanelState?.({ isOpen, side });
    return () => setPanelState?.(null);
  }, [isOpen, side, setPanelState]);

  const handleToggle = () => {
    const newState = !isOpen;
    if (isControlled) {
      onOpenChange?.(newState);
    } else {
      setInternalIsOpen(newState);
    }
  };

  const isRight = side === 'right';
  const ChevronIcon = isOpen
    ? isRight
      ? ChevronLeft
      : ChevronRight
    : isRight
      ? ChevronRight
      : ChevronLeft;

  return (
    <>
      {/* Toggle button */}
      <button
        type="button"
        onClick={handleToggle}
        style={
          // Open: past the panel, which itself sits a pixel in, overlapping its outer border so none shows on the arrow.
          isOpen
            ? isRight
              ? { right: `calc(-${width.replace('w-', '')}*0.25rem - 1.25rem + 2px)` }
              : { left: `calc(-${width.replace('w-', '')}*0.25rem - 1.25rem + 2px)` }
            : undefined
        }
        className={cn(
          'absolute z-50 flex h-8 w-5 items-center justify-center border border-border bg-background hover:bg-accent transition-all duration-300',
          buttonPosition === 'top' ? 'top-4' : 'bottom-4',
          isRight ? 'rounded-r-md border-l-0' : 'rounded-l-md border-r-0',
          !isOpen && (isRight ? 'right-[calc(-1.25rem+1px)]' : 'left-[calc(-1.25rem+1px)]'),
        )}
        aria-label={isOpen ? 'Close panel' : 'Open panel'}
      >
        <ChevronIcon className="h-4 w-4" />
      </button>

      {/* Panel */}
      <div
        data-slot="dialog-panel"
        className={cn(dialogPanelVariants({ side, isOpen }), width, className)}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DIALOG_VIEWPORT_ANCHOR_CLASS,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPanel,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
